'use client'

import CloseIcon from '@/components/icons/close'
import useId from '@/hooks/use-id'
import generateClassNames from '@/utils/generate-class-names'
import clsx from 'clsx'
import Button from '../button'
import IconButton from '../icon-button'
import Modal, { type ModalProps } from '../modal'
import './alert-dialog.scss'

const classes = generateClassNames('alert-dialog', [
  'title',
  'description',
  'actions',
  'close',
])

export interface AlertDialogProps extends Omit<ModalProps, 'title'> {
  cancelText?: React.ReactNode
  confirmText?: React.ReactNode
  description?: React.ReactNode
  onCancel?: () => void
  onConfirm?: () => void
  title?: React.ReactNode
}

function AlertDialog(props: AlertDialogProps) {
  const {
    cancelText = 'cancel',
    className,
    confirmText = 'Confirm',
    description,
    onCancel,
    onConfirm,
    onOpenChange,
    title,
    ...other
  } = props

  const labelledBy = useId()
  const describedBy = useId()

  const handleConfirm = () => {
    onOpenChange?.(false)
    onConfirm?.()
  }

  const handleCancel = () => {
    onOpenChange?.(false)
    onCancel?.()
  }

  return (
    <Modal
      aria-describedby={describedBy}
      aria-labelledby={labelledBy}
      className={clsx(classes.root, className)}
      onOpenChange={onOpenChange}
      role='alertdialog'
      {...other}
    >
      <div className={classes.close}>
        <IconButton aria-label='Close' onClick={handleCancel} size='sm'>
          <CloseIcon />
        </IconButton>
      </div>
      <h2 className={classes.title} id={labelledBy}>
        {title}
      </h2>
      <p className={classes.description} id={describedBy}>
        {description}
      </p>
      <div className={classes.actions}>
        <Button size='lg' variant='neutral' fullWidth onClick={handleCancel}>
          {cancelText}
        </Button>
        <Button size='lg' variant='default' fullWidth onClick={handleConfirm}>
          {confirmText}
        </Button>
      </div>
    </Modal>
  )
}

export default AlertDialog
